import { ChevronDown, ChevronUp, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { commitHandlers, fieldInvalid, rowErrorMessages } from "./row-fields";
import { RowSaveFooter, RowStatusIcon } from "./RowSaveState";
import { fieldClass } from "./fields";
import type { CatalogEditor, EditorRow } from "./useCatalogEditor";

/**
 * The top of a category card: its name, edited in place and saved like any
 * other field; the control to order the categories (a grip on desktop, ▲▼ on a
 * phone); the product count; and its own "⋯" — «Desactivar categoría», which
 * sends its products to the root and asks first.
 */
export function CategoryHeader({
  editor,
  row,
  productCount,
  canMoveUp,
  canMoveDown,
  onMove,
  onRemove,
  dragMode = false,
  dragHandle,
}: {
  editor: CatalogEditor;
  row: EditorRow<"categories">;
  productCount: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (delta: number) => void;
  onRemove: () => void;
  /** Desktop: the card is ordered by dragging, so the arrows give way to the grip. */
  dragMode?: boolean;
  /** The grip, when this card can be dragged (it is saved). */
  dragHandle?: React.ReactNode;
}): React.JSX.Element {
  const name = row.current.name.trim() || "esta categoría";
  const saved = row.id !== null;
  const errors = rowErrorMessages(editor, "categories", row.key);

  return (
    <div className="border-b border-ink-050 bg-ink-050/60">
      <div className="flex items-center gap-2 px-4 py-2.5">
        {dragMode && (dragHandle ?? <span aria-hidden className="-ml-2 w-[18px] shrink-0" />)}
        {!dragMode && (
          <div className="-ml-1.5 flex shrink-0 flex-col">
            <button
              type="button"
              aria-label={`Subir la categoría ${name}`}
              disabled={!saved || !canMoveUp}
              onClick={() => onMove(-1)}
              className="rounded p-0.5 text-ink-300 hover:bg-ink-100 hover:text-ink-700 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronUp size={14} />
            </button>
            <button
              type="button"
              aria-label={`Bajar la categoría ${name}`}
              disabled={!saved || !canMoveDown}
              onClick={() => onMove(1)}
              className="rounded p-0.5 text-ink-300 hover:bg-ink-100 hover:text-ink-700 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronDown size={14} />
            </button>
          </div>
        )}
        <input
          className={`${fieldClass({
            invalid: fieldInvalid(editor, "categories", row.key, "name"),
          })} min-w-[160px] flex-1 font-semibold`}
          value={row.current.name}
          placeholder="Nombre de la categoría (p. ej. Primaria)"
          aria-label={`Nombre de la categoría ${name}`}
          onChange={(e) => editor.edit("categories", row.key, { name: e.target.value }, "debounce")}
          {...commitHandlers(editor, "categories", row.key)}
        />
        <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold tabular-nums text-ink-500">
          {productCount}
        </span>
        <span className="flex h-6 w-5 items-center justify-center">
          <RowStatusIcon status={row.status} />
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Opciones de la categoría ${name}`}
              className="flex h-6 w-6 items-center justify-center rounded text-ink-400 hover:bg-ink-100 hover:text-ink-600 focus:outline-none"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onRemove}>
              {saved ? "Desactivar categoría" : "Quitar"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {errors.map((message) => (
        <p key={message} className="px-4 pb-2 text-[12px] text-red-600">
          {message}
        </p>
      ))}
      <RowSaveFooter editor={editor} list="categories" row={row} />
    </div>
  );
}
